import React, { useEffect, useMemo, useState } from "react";
import http from "../../http-common";
import "./SystemInformation.css";
import {
  xrvaAvStatus,
  xrvaKillAll,
  xrvaListStreams,
  xrvaStopAudio,
  xrvaStopVideo,
} from "../../api/xrvaApi";
import { useAuth } from "../../auth/AuthContext";
import NetplanWifiManager from "./NetplanWifiManager";

// Helper: format timestamps
const formatTime = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso || "-";
  }
};

// Small component: Modal (no external deps)
function KvModal({ open, title, children, onClose }) {
  if (!open) return null;
  return (
    <div className="kvsys-3f9c-modal-backdrop" onClick={onClose}>
      <div className="kvsys-3f9c-modal" onClick={(e) => e.stopPropagation()}>
        <div className="kvsys-3f9c-modal-header">
          <h3 className="kvsys-3f9c-modal-title">{title}</h3>
          <button
            className="kvsys-3f9c-iconbtn"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="kvsys-3f9c-modal-body">{children}</div>
      </div>
    </div>
  );
}

function SystemStatus() {
  const { userID } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [avStatus, setAvStatus] = useState({
    video: false,
    audio: false,
    urls: { video: {}, audio: {} },
  });
  const [webrtcActive, setWebrtcActive] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);

  // Logout modal state
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [logoutForm, setLogoutForm] = useState({ userId: "", passcode: "" });
  const [logoutBusy, setLogoutBusy] = useState(false);
  const [logoutMessage, setLogoutMessage] = useState("");

  const getDBInfo = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await http.get("/api/system/db-info");
      setData(data);
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.message || err?.message || "Failed to load."
      );
    } finally {
      setLoading(false);
    }
  };

  const getRunningStatus = async () => {
    try {
      const status = await xrvaAvStatus(); // { video:boolean, audio:boolean, urls:{...} }
      const streams = await xrvaListStreams(); // { items: [...] }
      setAvStatus(
        status || { video: false, audio: false, urls: { video: {}, audio: {} } }
      );
      setWebrtcActive(
        Array.isArray(streams?.items) && streams.items.length > 0
      );
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.message || err?.message || "Failed to load."
      );
    }
  };

  const logoutTargetedUser = async (userId, passcode) => {
    setLogoutBusy(true);
    setLogoutMessage("");
    try {
      const { data } = await http.post("/api/system/logout-targeted-user", {
        targetUserId: userId,
        passcode,
      });
      setLogoutMessage(
        typeof data === "string"
          ? data
          : data?.message || "Logout request sent."
      );
      setLogoutOpen(false);
      // Refresh data after action
      await getDBInfo();
    } catch (err) {
      console.error(err);
      setLogoutMessage(
        err?.response?.data?.message || err?.message || "Logout failed."
      );
    } finally {
      setLogoutBusy(false);
    }
  };

  useEffect(() => {
    getDBInfo();
    getRunningStatus();
  }, []);

  const loggedUsers = useMemo(() => data?.LOGGED_USERS || [], [data]);
  const startedVideo = data?.STARTED_VIDEO || null;

  const startedAudio = data?.STARTED_AUDIO || null;
  const startedWebRTC = data?.STARTED_WEB_RTC || null;

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert("Copied to clipboard");
    } catch {
      alert("Copy failed");
    }
  };

  const stopVideo = async () => {
    setActionBusy(true);
    try {
      await xrvaStopVideo();
      await getRunningStatus();
      await getDBInfo();
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.message || e?.message || "Failed to stop video."
      );
    } finally {
      setActionBusy(false);
    }
  };

  const stopAudio = async () => {
    setActionBusy(true);
    try {
      await xrvaStopAudio();
      await getRunningStatus();
      await getDBInfo();
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.message || e?.message || "Failed to stop audio."
      );
    } finally {
      setActionBusy(false);
    }
  };

  const stopAll = async () => {
    setActionBusy(true);
    try {
      await xrvaStopVideo();
      await xrvaStopAudio();
      await xrvaKillAll();
      await getRunningStatus();
      await getDBInfo();
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.message || e?.message || "Failed to stop everything."
      );
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <div className="kvsys-3f9c-wrap">
      <div className="kvsys-3f9c-container">
        <div className="kvsys-3f9c-header">
          <div className="kvsys-3f9c-h1">System Information</div>
          {loading && <div className="kvsys-3f9c-loader" />}
        </div>

        <div className="kvsys-3f9c-toolbar">
          <button
            className="kvsys-3f9c-btn primary"
            onClick={async () => {
              await getDBInfo();
              await getRunningStatus();
            }}
            disabled={loading}
          >
            ⟳ Refresh Data
          </button>
          <button
            className="kvsys-3f9c-btn"
            onClick={() => setLogoutOpen(true)}
          >
            ⇥ Logout by ID
          </button>
          <button
            className="kvsys-3f9c-btn accent"
            onClick={() => window.location.reload()}
          >
            ↻ Hard Refresh Page
          </button>
        </div>

        {error && <div className="kvsys-3f9c-err">{error}</div>}

        <div className="kvsys-3f9c-grid">
          {/* XRVA Status */}
          <section className="kvsys-3f9c-card sm6">
            <h2>XRVA Status</h2>
            <div className="kvsys-3f9c-sub">
              Live flags and WebRTC sessions.
            </div>

            <div className="kvsys-3f9c-kv">
              <div>Video</div>
              <div>
                <span className="kvsys-3f9c-chip">
                  {avStatus.video ? "Running" : "Stopped"}
                </span>
                {avStatus.video && (
                  <button
                    className="kvsys-3f9c-btn danger"
                    style={{ marginLeft: 8 }}
                    onClick={stopVideo}
                    disabled={actionBusy}
                  >
                    {actionBusy ? "Working…" : "Stop Video"}
                  </button>
                )}
              </div>
            </div>

            <div className="kvsys-3f9c-kv">
              <div>Audio</div>
              <div>
                <span className="kvsys-3f9c-chip">
                  {avStatus.audio ? "Running" : "Stopped"}
                </span>
                {avStatus.audio && (
                  <button
                    className="kvsys-3f9c-btn danger"
                    style={{ marginLeft: 8 }}
                    onClick={stopAudio}
                    disabled={actionBusy}
                  >
                    {actionBusy ? "Working…" : "Stop Audio"}
                  </button>
                )}
              </div>
            </div>

            <div className="kvsys-3f9c-kv">
              <div>WebRTC</div>
              <div>
                <span className="kvsys-3f9c-chip">
                  {webrtcActive ? "Active" : "None"}
                </span>
              </div>
            </div>

            <div className="kvsys-3f9c-actions" style={{ marginTop: 8 }}>
              <button
                className="kvsys-3f9c-btn danger"
                onClick={stopAll}
                disabled={
                  actionBusy ||
                  (!avStatus.video && !avStatus.audio && !webrtcActive)
                }
              >
                {actionBusy ? "Working…" : "Kill All Processes"}
              </button>
            </div>
          </section>
          {/* Logged Users */}
          <section className="kvsys-3f9c-card">
            <h2>Logged Users</h2>
            <div className="kvsys-3f9c-sub">
              Active sessions currently in the system.
            </div>
            {loggedUsers.length === 0 ? (
              <div className="kvsys-3f9c-empty">No active users.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="kvsys-3f9c-table kvsys-3f9c-table--responsive">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Username</th>
                      <th>IP</th>
                      <th>User Agent</th>
                      <th>Login Time</th>
                      <th>Host</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loggedUsers.map((u) => (
                      <tr key={u.id} data-label="User">
                        <td>
                          {u.id === userID && (
                            <p>
                              <strong>Your session</strong>
                            </p>
                          )}
                          <div style={{ fontWeight: 600 }}>
                            {u.fullName || "-"}
                          </div>
                          <div className="kvsys-3f9c-sub">{u.id}</div>
                        </td>
                        <td data-label="Username">{u.username || "-"}</td>
                        <td data-label="IP">
                          {u.ip || "-"}
                          <br />
                          {u.geo && u.geo.city !== "-" ? (
                            <>
                              <br />
                              {u.geo.city}, {u.geo.region}, {u.geo.zipCode},{" "}
                              {u.geo.countryShort}
                              <br />
                              <a
                                href={`https://www.google.com/maps?q=${u.geo.latitude},${u.geo.longitude}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Open in Google Maps
                              </a>
                              <br />
                              <br />
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: u.geo.credits,
                                }}
                              />
                            </>
                          ) : (
                            <>
                              <br />
                              (no geo info found for this IP)
                            </>
                          )}
                        </td>
                        <td data-label="User Agent">
                          <div className="kvsys-ua" title={u.userAgent || ""}>
                            {u.userAgent || "-"}
                          </div>
                          {u.userAgent && (
                            <button
                              className="kvsys-3f9c-iconbtn"
                              onClick={() => copyToClipboard(u.userAgent)}
                            >
                              Copy
                            </button>
                          )}
                        </td>
                        <td data-label="Login Time">
                          {formatTime(u.loginTime)}
                        </td>
                        <td data-label="Host">{u.host || "-"}</td>
                        <td data-label="Actions">
                          <div className="kvsys-3f9c-actions">
                            {u.id === userID ? (
                              <p className="lougoutStatusMessage">
                                To log out of your session, click the Logout
                                button in the navigation bar.
                              </p>
                            ) : (
                              <button
                                className="kvsys-3f9c-btn danger"
                                onClick={() => {
                                  setLogoutOpen(true);
                                  setLogoutForm((f) => ({
                                    ...f,
                                    userId: u.id,
                                  }));
                                }}
                              >
                                Logout
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Streams */}
          <section className="kvsys-3f9c-card sm6">
            <h2>Started Video</h2>
            {!startedVideo ? (
              <div className="kvsys-3f9c-empty">No active video stream.</div>
            ) : (
              <>
                <div className="kvsys-3f9c-sub">
                  Started at: {formatTime(startedVideo.startedAt)}
                </div>
                <div className="kvsys-3f9c-kv">
                  <div>User</div>
                  <div>
                    {startedVideo.user?.fullName}{" "}
                    <span className="kvsys-3f9c-sub">
                      ({startedVideo.user?.id})
                    </span>
                  </div>
                </div>
                <div className="kvsys-3f9c-kv">
                  <div>Source is: </div>
                  <div>
                    {startedVideo.urls ? "VIDEO and AUDIO" : "VIDEO only"}
                  </div>
                </div>
                {startedVideo.urls && (
                  <div className="kvsys-3f9c-kv">
                    <div>LLHLS</div>
                    <div>
                      <code>{startedVideo.urls?.llhls}</code>
                      {startedVideo.urls?.llhls && (
                        <button
                          className="kvsys-3f9c-iconbtn"
                          onClick={() =>
                            copyToClipboard(startedVideo.urls.llhls)
                          }
                        >
                          Copy
                        </button>
                      )}
                    </div>
                  </div>
                )}
                <div className="kvsys-3f9c-kv">
                  <div>RTSP</div>
                  <div>
                    <code>{startedVideo.urls?.rtsp}</code>
                    {startedVideo.urls?.rtsp && (
                      <button
                        className="kvsys-3f9c-iconbtn"
                        onClick={() => copyToClipboard(startedVideo.urls.rtsp)}
                      >
                        Copy
                      </button>
                    )}
                  </div>
                </div>
                <div className="kvsys-3f9c-kv">
                  <div>HLS</div>
                  <div>
                    <code>{startedVideo.urls?.hls}</code>
                    {startedVideo.urls?.hls && (
                      <button
                        className="kvsys-3f9c-iconbtn"
                        onClick={() => copyToClipboard(startedVideo.urls.hls)}
                      >
                        Copy
                      </button>
                    )}
                  </div>
                </div>
                <div className="kvsys-3f9c-kv">
                  <div>WebRTC</div>
                  <div>
                    <code>{startedVideo.urls?.webrtc}</code>
                    {startedVideo.urls?.webrtc && (
                      <button
                        className="kvsys-3f9c-iconbtn"
                        onClick={() =>
                          copyToClipboard(startedVideo.urls.webrtc)
                        }
                      >
                        Copy
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </section>

          <section className="kvsys-3f9c-card sm6">
            <h2>Started Audio</h2>
            {!startedAudio ? (
              <div className="kvsys-3f9c-empty">No active audio stream.</div>
            ) : (
              <>
                <div className="kvsys-3f9c-sub">
                  Started at: {formatTime(startedAudio.startedAt)}
                </div>
                <div className="kvsys-3f9c-kv">
                  <div>User</div>
                  <div>
                    {startedAudio.user?.fullName}{" "}
                    <span className="kvsys-3f9c-sub">
                      ({startedAudio.user?.id})
                    </span>
                  </div>
                </div>
                <div className="kvsys-3f9c-kv">
                  <div>HLS</div>
                  <div>
                    <code>{startedAudio.urls?.hls}</code>
                    {startedAudio.urls?.hls && (
                      <button
                        className="kvsys-3f9c-iconbtn"
                        onClick={() => copyToClipboard(startedAudio.urls.hls)}
                      >
                        Copy
                      </button>
                    )}
                  </div>
                </div>
                <div className="kvsys-3f9c-kv">
                  <div>Page</div>
                  <div>
                    <code>{startedAudio.urls?.page}</code>
                    {startedAudio.urls?.page && (
                      <button
                        className="kvsys-3f9c-iconbtn"
                        onClick={() => copyToClipboard(startedAudio.urls.page)}
                      >
                        Copy
                      </button>
                    )}
                  </div>
                </div>
                <div className="kvsys-3f9c-kv">
                  <div>WebRTC</div>
                  <div>
                    <code>{startedAudio.urls?.webrtc}</code>
                    {startedAudio.urls?.webrtc && (
                      <button
                        className="kvsys-3f9c-iconbtn"
                        onClick={() =>
                          copyToClipboard(startedAudio.urls.webrtc)
                        }
                      >
                        Copy
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </section>

          <section className="kvsys-3f9c-card sm6">
            <h2>Started WebRTC</h2>
            {!startedWebRTC ? (
              <div className="kvsys-3f9c-empty">No active WebRTC session.</div>
            ) : (
              <>
                <div className="kvsys-3f9c-sub">
                  Started at: {formatTime(startedWebRTC.startedAt)}
                </div>
                <div className="kvsys-3f9c-kv">
                  <div>User</div>
                  <div>
                    {startedWebRTC.user?.fullName}{" "}
                    <span className="kvsys-3f9c-sub">
                      ({startedWebRTC.user?.id})
                    </span>
                  </div>
                </div>
                <div className="kvsys-3f9c-kv">
                  <div>Session</div>
                  <div>
                    <code>{startedWebRTC.id}</code>
                    {startedWebRTC.id && (
                      <button
                        className="kvsys-3f9c-iconbtn"
                        onClick={() => copyToClipboard(startedWebRTC.id)}
                      >
                        Copy
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
      <NetplanWifiManager
        baseUrl="/system"
        apiKey={"process.env.REACT_APP_API_KEY"}
      />

      {/* Logout Modal */}
      <KvModal
        open={logoutOpen}
        title="Logout targeted user"
        onClose={() => setLogoutOpen(false)}
      >
        <div className="kvsys-3f9c-field">
          <label>User ID</label>
          <input
            className="kvsys-3f9c-input"
            placeholder="b1c95701ef475e93cbad45e2318983dc"
            value={logoutForm.userId}
            onChange={(e) =>
              setLogoutForm({ ...logoutForm, userId: e.target.value })
            }
          />
        </div>
        <div className="kvsys-3f9c-field">
          <label>Admin Passcode</label>
          <input
            className="kvsys-3f9c-input"
            type="password"
            placeholder="••••••"
            value={logoutForm.passcode}
            onChange={(e) =>
              setLogoutForm({ ...logoutForm, passcode: e.target.value })
            }
          />
        </div>
        {logoutMessage && (
          <div className="kvsys-3f9c-sub" style={{ marginBottom: 8 }}>
            {logoutMessage}
          </div>
        )}
        <div className="kvsys-3f9c-footer">
          <button
            className="kvsys-3f9c-btn"
            onClick={() => setLogoutOpen(false)}
          >
            Cancel
          </button>
          <button
            className="kvsys-3f9c-btn danger"
            disabled={!logoutForm.userId || !logoutForm.passcode || logoutBusy}
            onClick={() =>
              logoutTargetedUser(logoutForm.userId, logoutForm.passcode)
            }
          >
            {logoutBusy ? "Working…" : "Logout User"}
          </button>
        </div>
      </KvModal>
    </div>
  );
}

export default SystemStatus;
